/**
 * client/src/components/recovery-freeze-banner.tsx
 *
 * #1239 Task 8 [AC-REC-08] — VICTIM FREEZE control.
 *
 * Rendered in the authenticated app shell. When the server broadcasts a
 * `recovery:freeze_offer` to a recovery subject's live sessions (the instant a
 * guardian recovery ceremony is initiated), this banner surfaces a prominent
 * "recovery in progress — was this you?" warning with a one-tap "Freeze now"
 * that aborts the pending ceremony (POST /api/recovery/freeze). A victim still
 * holding any device kills an attacker-initiated recovery instantly — the
 * load-bearing real-time control.
 *
 * Colour: AMBER (a user-protective WARNING, not a Forensic Pause). Per
 * DESIGN-SYSTEM.md / CLAUDE.md, RED is reserved for Forensic Pause + critical
 * OSINT only; "was this you?" is a warning the user resolves, so it uses amber.
 *
 * Animation honours prefers-reduced-motion via Framer Motion's reduced-motion
 * handling (the entrance is a short opacity/translate; ≤350ms, no layout shift).
 */

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, ShieldOff } from "lucide-react";
import { postJSON } from "@/lib/queryClient";
import { useRecoveryFreezeOffer } from "@/lib/use-recovery-freeze-offer";

export const RECOVERY_FREEZE_BANNER_ID = "recovery-freeze-banner";

interface Props {
  /** The current user's id — used for the WS auth handshake. Null = no listener. */
  userId: string | null | undefined;
}

export function RecoveryFreezeBanner({ userId }: Props) {
  const { offer, clear } = useRecoveryFreezeOffer(userId);
  const [freezing, setFreezing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleFreeze(): Promise<void> {
    if (!offer) return;
    setFreezing(true);
    setError(null);
    try {
      await postJSON("/api/recovery/freeze", { sessionId: offer.sessionId });
      // Freeze landed — the ceremony is terminal and releases no shares. Clear
      // the banner. (Idempotent server-side, so a double-tap is harmless.)
      clear();
    } catch (e) {
      setError(
        e instanceof Error
          ? "Couldn't freeze just now — try again."
          : "Couldn't freeze just now — try again.",
      );
    } finally {
      setFreezing(false);
    }
  }

  return (
    <AnimatePresence>
      {offer && (
        <motion.div
          id={RECOVERY_FREEZE_BANNER_ID}
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -16 }}
          transition={{ duration: 0.22, ease: "easeOut" }}
          className="w-full border-b-2 border-amber-500 bg-amber-950"
          role="alert"
          aria-live="assertive"
        >
          <div className="mx-auto flex max-w-5xl items-start gap-3 px-4 py-3">
            <AlertTriangle
              className="mt-0.5 h-5 w-5 shrink-0 text-amber-400"
              aria-hidden="true"
            />

            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold leading-tight text-amber-200">
                Account recovery in progress — was this you?
              </p>
              <p className="mt-0.5 text-xs leading-snug text-amber-300">
                Someone started recovering your vault through your guardians. If
                this wasn&apos;t you, freeze it now — that stops your guardians from
                releasing any key shares.
              </p>

              {error && (
                <p className="mt-1 text-xs font-medium text-amber-400">{error}</p>
              )}
            </div>

            <div className="flex shrink-0 items-center gap-2">
              <motion.button
                type="button"
                whileTap={{ scale: 0.96 }}
                onClick={handleFreeze}
                disabled={freezing}
                className="flex items-center gap-1.5 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-amber-50 transition-colors hover:bg-amber-500 disabled:opacity-60"
              >
                <ShieldOff className="h-3.5 w-3.5" aria-hidden="true" />
                {freezing ? "Freezing…" : "Freeze now"}
              </motion.button>
              <button
                type="button"
                onClick={clear}
                disabled={freezing}
                className="rounded-md px-2 py-1.5 text-xs text-amber-300/80 transition-colors hover:text-amber-200 disabled:opacity-60"
              >
                This was me
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
